import { useTranslation } from 'react-i18next';
import { Tags } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { DimensionValuePicker } from '../../../shared/components/accounting/DimensionValuePicker';
import type { Dimension, LineDimensionAssignment } from '../types';

interface LineDimensionsCellProps {
  /** Applicable axes for this line type (already filtered by applies_to_*). */
  dimensions: Dimension[];
  /** Current per-line assignments. */
  value: LineDimensionAssignment[];
  onChange: (next: LineDimensionAssignment[]) => void;
  disabled?: boolean;
}

/**
 * Compact per-line analytic-dimension assignment control. A popover holds one
 * value picker per applicable axis; the trigger shows how many are assigned so
 * the line table stays narrow at 375px. Used by the journal-entry and bill
 * line editors. Only axes where the relevant applies_to flag is true are passed.
 */
export function LineDimensionsCell({ dimensions, value, onChange, disabled }: LineDimensionsCellProps) {
  const { t } = useTranslation('accounting');

  const valueFor = (dimensionId: string): string =>
    value.find((a) => a.dimension_id === dimensionId)?.dimension_value_id ?? '';

  const setValue = (dimensionId: string, valueId: string) => {
    const others = value.filter((a) => a.dimension_id !== dimensionId);
    onChange(valueId ? [...others, { dimension_id: dimensionId, dimension_value_id: valueId }] : others);
  };

  const assignedCount = value.filter((a) =>
    dimensions.some((d) => d.id === a.dimension_id),
  ).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn('w-full justify-start font-normal', assignedCount === 0 && 'text-muted-foreground')}
          title={t('dimensions.lineAssignmentTitle')}
        >
          <Tags className="me-2 h-3.5 w-3.5 shrink-0" />
          {assignedCount > 0 ? (
            <Badge variant="secondary" className="px-1.5 py-0 text-xs">
              {assignedCount}
            </Badge>
          ) : (
            <span className="truncate">{t('dimensions.noDimensionsAssigned')}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" align="start">
        <p className="text-sm font-medium">{t('dimensions.lineAssignmentTitle')}</p>
        {dimensions.map((d) => (
          <div key={d.id} className="space-y-1.5">
            <Label className="text-xs">
              {d.name}
              {d.is_required && <span className="text-destructive ms-1">*</span>}
            </Label>
            <DimensionValuePicker
              dimensionId={d.id}
              value={valueFor(d.id)}
              onChange={(valueId) => setValue(d.id, valueId)}
              allowClear
              hideAddNew
            />
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
